import { environment, Environment } from "app/environment/environment";
import { format } from "date-fns";
import { InfrastructureForm } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { calendarDateTimeToDate } from "shared/utils/dateUtils";

type FieldIds = {
  incidentId: string;
  date: string; // Format: "YYYY-MM-DD"
  startAndEndTime: string; // Format: "HH:mm:ss"
  eventType: string;
  stretchName: string;
};

// Field IDs are generated in Microsoft Forms, and must be updated if the form changes.
const FIELD_IDS_TEST: FieldIds = {
  incidentId: "r71b8354d2e894a93b23ccd286eeeaa5a",
  date: "r956eac9358b24e1b99aa139a26a6dce2",
  startAndEndTime: "r6469697b14a64ba19a69ea2b45a14f11",
  eventType: "r1ddd5fe023f84e16a66e11317c891d7f",
  stretchName: "r039f45964e3d4664a065c2f02bdb7322",
};
const FIELD_IDS_PROD: FieldIds = {
  incidentId: "r71b8354d2e894a93b23ccd286eeeaa5a",
  date: "r956eac9358b24e1b99aa139a26a6dce2",
  startAndEndTime: "r6469697b14a64ba19a69ea2b45a14f11",
  eventType: "r1ddd5fe023f84e16a66e11317c891d7f",
  stretchName: "r039f45964e3d4664a065c2f02bdb7322",
};

const FORM_ID_TEST =
  "8s9UHilkFEq7GVBLZ5BAWkPeD6LqNChEkaLf4LGso2RUNVhKTUcyTFJMRzkzNDZTVjZNTFMzRzFGTSQlQCN0PWcu";
const FORM_ID_PROD =
  "8s9UHilkFEq7GVBLZ5BAWkPeD6LqNChEkaLf4LGso2RUQTBPSUdPWFJKMkhUSEQ1NjNXN0MwVlQ4VyQlQCN0PWcu";

const getEventTypeLabel = (type: string): string => {
  switch (type) {
    case "INFRASTRUCTURE_STRETCH_CLOSED":
      return "Strekning stengt";
    case "INFRASTRUCTURE_STRETCH_PARTIALLY_CLOSED":
      return "Strekning delvis stengt";
    default:
      return type;
  }
};

const getDateAndTime = (
  infrastructureForm: InfrastructureForm["infrastructureForm"],
): { date: string; startAndEndTime: string } => {
  const { durations } = infrastructureForm;
  if (durations.type === "OPEN_ENDED") {
    const date = calendarDateTimeToDate(durations.from_time);
    return {
      date: format(date, "yyyy-MM-dd"),
      startAndEndTime: format(date, "HH:mm"),
    };
  }

  if (durations.type === "TIMED") {
    const fromDate = calendarDateTimeToDate(durations.from_time);
    const toDate = calendarDateTimeToDate(durations.to_time);
    return {
      date: format(fromDate, "yyyy-MM-dd"),
      startAndEndTime: `${format(fromDate, "HH:mm")}-${format(toDate, "HH:mm")}`,
    };
  }
  // Evaluation form is not relevant for planned events, which is the only type that has PERIODIC durations
  return { date: "", startAndEndTime: "" };
};

export const buildEvaluationFormUrl = (
  incidentId: string,
  infrastructureForm: InfrastructureForm["infrastructureForm"],
): string => {
  const env: Environment = environment();
  const formId = env === Environment.Production ? FORM_ID_PROD : FORM_ID_TEST;
  const fieldIds =
    env === Environment.Production ? FIELD_IDS_PROD : FIELD_IDS_TEST;
  const { date, startAndEndTime } = getDateAndTime(infrastructureForm);
  const encodedStartAndEndTime = encodeURIComponent(startAndEndTime);
  const encodedIncidentId = encodeURIComponent(incidentId);
  const encodedEventTypeLabel = encodeURIComponent(
    getEventTypeLabel(infrastructureForm.type),
  );
  const encodedStretchName = encodeURIComponent(infrastructureForm.stretchName);
  return `https://forms.office.com/Pages/ResponsePage.aspx?id=${formId}&${fieldIds.incidentId}=${encodedIncidentId}&${fieldIds.date}=%22${date}%22&${fieldIds.startAndEndTime}=${encodedStartAndEndTime}&${fieldIds.eventType}=${encodedEventTypeLabel}&${fieldIds.stretchName}=${encodedStretchName}`;
};

export const contextsForEvaluationForm = ["DROPS", "TRAIN_DRIVER", "CONDUCTOR"];
